import React from 'react';
import { Truck, PackageCheck, Ban, Box, ScanSearch, AlertTriangle, Undo2, ReceiptIndianRupee, Globe2, Headset, RefreshCcw, Timer } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { SUPPORT_EMAIL, SUPPORT_EMAIL_MAILTO } from '@/config/links';

const ShippingPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Shipping Policy"
        description="Shipping policy and service levels for Boxd Logistics."
        canonical="https://boxdlogistics.in/shipping-policy"
        robots="noindex, follow"
      />
      <Navbar />

      <section className="relative pt-32 pb-16 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        ></div>
        <div className="absolute -top-12 right-0 w-72 h-72 bg-gradient-to-br from-blue-300/20 to-blue-700/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 text-blue-900 px-4 py-2 text-sm font-semibold mb-6">
            <Truck className="w-4 h-4" />
            Shipping Policy
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">Shipping Policy</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl">
            Please review our shipping policies carefully to understand our processes, partner responsibilities, and your rights as a user.
          </p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Service Scope</h2>
            <p className="text-gray-600 leading-relaxed">
              We provide a platform that allows businesses and individuals to compare, book, and manage shipments with multiple courier providers through a single interface. All logistics and delivery services are fulfilled by our partnered courier companies.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/30 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <PackageCheck className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">2. Pickup & Delivery</h2>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li>
                <strong>Pickup Availability:</strong> Pickup services are offered based on the selected courier&apos;s serviceable pin codes. You must schedule pickups within the defined time slots.
              </li>
              <li>
                <strong>Delivery Time:</strong> Delivery estimates are provided by courier partners and vary based on origin, destination, and service type (standard, express, same-day, etc.).
              </li>
              <li>
                <strong>Delivery Attempt:</strong> Most courier partners make up to 3 delivery attempts before the shipment is returned to the origin.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <Ban className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">3. Shipping Restrictions</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Prohibited items include hazardous materials, illegal goods, perishable food without proper packaging, currency, and valuable items (unless insured). Customers are responsible for ensuring that the shipment complies with local, national, and international shipping regulations.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <Box className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">4. Packaging Guidelines</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Customers must ensure the shipment is securely packaged. Improper packaging may lead to damage or refusal by courier partners during pickup. Fragile or high-value items should be packed with sufficient cushioning and clearly marked.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <ScanSearch className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">5. Tracking</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Real-time tracking is available via our dashboard. Tracking information is synced from our courier partners and updated automatically as events occur during transit.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">6. Delays & Service Interruptions</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              We are not liable for delays caused by the courier, natural calamities, political unrest, or customs clearance. We do, however, help you escalate issues to the respective courier partner.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Damaged or Lost Shipments</h2>
            <p className="text-gray-600 leading-relaxed">
              Claims for damage or loss must be filed within 48 hours of delivery or expected delivery date. Claim resolution is subject to the policies of the respective courier partner. Photographic evidence and original packaging may be required for processing claims.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <Undo2 className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">8. Return to Origin (RTO)</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              If a shipment is undeliverable, it will be returned to the sender based on courier partner policies. RTO charges may apply and are borne by the sender.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <ReceiptIndianRupee className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">9. Cancellations & Refunds</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Shipment bookings can only be canceled before pickup. Once a shipment is picked up, cancellation may not be possible. Refunds are subject to courier approval and processing time, minus service fees if applicable.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <Globe2 className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">10. Customs & Duties (International Shipments)</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              The recipient is responsible for any customs duties or import taxes. Proper documentation must be provided for international shipments to avoid clearance delays.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/30 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <Headset className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">11. Support</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              For assistance with bookings, tracking, or claims, our customer support team is available at:
            </p>
            <ul className="mt-4 space-y-2 text-gray-700">
              <li><strong>Email:</strong> <a href={SUPPORT_EMAIL_MAILTO} className="text-blue-800 hover:text-blue-900">{SUPPORT_EMAIL}</a></li>
              <li><strong>Hours:</strong> Mon-Sat, 9:00 AM - 7:00 PM (IST)</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <RefreshCcw className="w-5 h-5 text-blue-900 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">12. Policy Updates</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to update this policy at any time. Changes will be communicated via email or on our website.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <Timer className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">13. Shipping Timeframes</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-4">
              Estimated delivery times may vary based on the selected shipping method and destination. Below are general timeframes offered by most courier partners:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li><strong>Basic Shipping:</strong> 5 to 7 business days (economical option for non-urgent deliveries).</li>
              <li><strong>Standard Shipping:</strong> 2 to 4 business days (balance of speed and cost).</li>
              <li><strong>Express Shipping:</strong> 1 to 2 business days (fastest delivery for urgent shipments).</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              Note: All delivery estimates are provided by courier partners and may be affected by factors such as weather, remote area access, public holidays, and customs delays.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ShippingPolicy;
